import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Download, Link2, Mail, MessageCircle, RefreshCw, Loader2, Copy, Ban, Eye } from "lucide-react";
import QuoteView from "./QuoteView";
import { API } from "./fq";

export const adminApi = async (token, path, { method = "GET", body } = {}) => {
  const r = await fetch(`${API}/api/franchise-quotes${path}${path.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`, {
    method, headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : d.detail?.message ? `${d.detail.message}: ${(d.detail.errors || []).map((e) => e.message).join("; ")}` : `Request failed (${r.status})`);
  return d;
};

const statusTone = { "Approved Proposal": "bg-green-100 text-green-800", Superseded: "bg-gray-200 text-gray-600", "Needs Review": "bg-amber-100 text-amber-800", Indicative: "bg-blue-100 text-blue-800" };

function SendDialog({ token, quote, open, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [step, setStep] = useState("edit");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) return;
    setStep("edit"); setResult(null);
    adminApi(token, `/quotes/${quote.id}/send/preview`, { method: "POST", body: { to_email: "" } }).then(setForm).catch((e) => toast.error(e.message));
  }, [open, quote, token]);
  const send = async () => {
    setBusy(true);
    try {
      const d = await adminApi(token, `/quotes/${quote.id}/send`, { method: "POST", body: { to_email: form.to_email, cc: form.cc || null, subject: form.subject, body: form.body, confirm: true } });
      setResult(d.delivery);
    } catch (e) { setResult({ status: "FAILED", error: e.message }); }
    setBusy(false); setStep("result"); onDone();
  };
  const f = (k, v) => setForm({ ...form, [k]: v });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="send-dialog">
        <DialogHeader><DialogTitle>Send quote {quote.quote_id} · Rev {quote.revision}</DialogTitle><DialogDescription>Nothing is sent until you confirm. Delivery status is recorded.</DialogDescription></DialogHeader>
        {!form ? <Loader2 className="w-5 h-5 animate-spin" /> : step === "edit" ? (
          <div className="space-y-3">
            {!form.smtp_ready && <div className="text-sm p-2 rounded bg-red-50 text-red-800" data-testid="send-smtp-unavailable">SMTP is not configured. You can still download or share the PDF.</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>To *</Label><Input data-testid="send-to" value={form.to_email || ""} onChange={(e) => f("to_email", e.target.value)} /></div>
              <div><Label>CC</Label><Input data-testid="send-cc" value={form.cc || ""} onChange={(e) => f("cc", e.target.value)} /></div>
            </div>
            <div><Label>Subject</Label><Input data-testid="send-subject" value={form.subject} onChange={(e) => f("subject", e.target.value)} /></div>
            <div><Label>Message</Label><Textarea data-testid="send-body" rows={8} value={form.body} onChange={(e) => f("body", e.target.value)} /></div>
            <p className="text-xs text-muted-foreground">From: {form.from} · Attachment: {form.attachment} (one combined PDF)</p>
          </div>
        ) : step === "confirm" ? (
          <div className="space-y-2 text-sm" data-testid="send-confirm-preview">
            <p><b>Recipient:</b> {form.to_email}{form.cc ? `, CC ${form.cc}` : ""}</p>
            <p><b>Subject:</b> {form.subject}</p>
            <p><b>Attachment:</b> {form.attachment}</p>
            <pre className="whitespace-pre-wrap bg-muted p-3 rounded text-xs max-h-60 overflow-auto">{form.body}</pre>
          </div>
        ) : (
          <div data-testid="send-result" className={`p-3 rounded text-sm ${result?.status === "ACCEPTED_BY_SMTP" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
            {result?.status === "ACCEPTED_BY_SMTP" ? `Accepted by the mail server for ${result.to}. (Inbox delivery is not confirmed by SMTP.)` : `Not sent: ${result?.error}`}
          </div>
        )}
        <DialogFooter>
          {step === "edit" && <Button data-testid="send-review" disabled={!form?.to_email || !form?.smtp_ready} onClick={() => setStep("confirm")}>Review before sending</Button>}
          {step === "confirm" && <><Button variant="outline" onClick={() => setStep("edit")}>Back</Button><Button data-testid="send-confirm" onClick={send} disabled={busy}>{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirm & Send</Button></>}
          {step === "result" && <Button onClick={onClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteActions({ token, q, enquiry, isSA, reload }) {
  const [sendOpen, setSendOpen] = useState(false);
  const [days, setDays] = useState("");
  const shareUrl = q.share && !q.share.revoked ? `${API}/api/public/franchise/shared/${q.share.token}` : null;
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(e.message); } };
  const download = () => window.open(`${API}/api/franchise-quotes/quotes/${q.id}/pdf?token=${encodeURIComponent(token)}`, "_blank");
  const wa = () => {
    const phone = (enquiry.contact?.phone || "").replace(/\D/g, "");
    if (!shareUrl) { toast.error("Create a share link first"); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Purnabramha indicative franchise quote ${q.quote_id} (Rev ${q.revision}): ${shareUrl}`)}`, "_blank");
    act(() => adminApi(token, `/quotes/${q.id}/whatsapp-opened`, { method: "POST" }), "WhatsApp opened — mark as sent only after you send it there");
  };
  const superseded = q.status === "Superseded";
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`quote-actions-${q.revision}`}>
      <Button size="sm" variant="outline" onClick={download} data-testid={`quote-pdf-${q.revision}`}><Download className="w-4 h-4 mr-1" />Combined PDF</Button>
      {!superseded && <>
        <Button size="sm" variant="outline" onClick={() => setSendOpen(true)} data-testid={`quote-send-${q.revision}`}><Mail className="w-4 h-4 mr-1" />Send email…</Button>
        <Input className="w-24 h-8" placeholder="Expiry days" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))} data-testid={`quote-share-days-${q.revision}`} />
        <Button size="sm" variant="outline" data-testid={`quote-share-${q.revision}`} onClick={() => act(() => adminApi(token, `/quotes/${q.id}/share`, { method: "POST", body: { expires_days: days ? Number(days) : null } }), "Share link created")}><Link2 className="w-4 h-4 mr-1" />{shareUrl ? "New link" : "Create share link"}</Button>
        {shareUrl && <>
          <Button size="sm" variant="ghost" data-testid={`quote-copy-${q.revision}`} onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied"); }}><Copy className="w-4 h-4 mr-1" />Copy</Button>
          <Button size="sm" variant="ghost" className="text-red-700" data-testid={`quote-revoke-${q.revision}`} onClick={() => act(() => adminApi(token, `/quotes/${q.id}/share/revoke`, { method: "POST" }), "Link revoked")}><Ban className="w-4 h-4 mr-1" />Revoke</Button>
        </>}
        <Button size="sm" variant="ghost" onClick={wa} data-testid={`quote-whatsapp-${q.revision}`}><MessageCircle className="w-4 h-4 mr-1" />Open WhatsApp</Button>
        <Select value={q.status} onValueChange={(v) => act(() => adminApi(token, `/quotes/${q.id}/status`, { method: "POST", body: { status: v } }), "Status updated")}>
          <SelectTrigger className="w-44 h-8" data-testid={`quote-status-${q.revision}`}><SelectValue /></SelectTrigger>
          <SelectContent>{["Indicative", "Needs Review", ...(isSA ? ["Approved Proposal"] : [])].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </>}
      {shareUrl && <p className="w-full text-xs text-muted-foreground break-all" data-testid={`quote-share-url-${q.revision}`}>{shareUrl} {q.share.expires_at ? `· expires ${q.share.expires_at.slice(0, 10)}` : "· no expiry"} · {q.share.views || 0} views</p>}
      <SendDialog token={token} quote={q} open={sendOpen} onClose={() => setSendOpen(false)} onDone={reload} />
    </div>
  );
}

export default function EnquiryDetail({ token, enquiryId, isSA, onChanged }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ status: "", assigned_to: "", next_follow_up: "", note: "" });
  const [viewRev, setViewRev] = useState(null);
  const load = useCallback(() => adminApi(token, `/enquiries/${enquiryId}`).then((x) => {
    setD(x);
    setForm({ status: x.enquiry.status, assigned_to: x.enquiry.assigned_to || "", next_follow_up: x.enquiry.next_follow_up || "", note: "" });
  }).catch((e) => toast.error(e.message)), [token, enquiryId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin" />;
  const e = d.enquiry, c = e.contact || {};
  const save = async () => {
    try { await adminApi(token, `/enquiries/${enquiryId}`, { method: "PATCH", body: form }); toast.success("Saved"); load(); onChanged?.(); } catch (err) { toast.error(err.message); }
  };
  const requote = async () => {
    try { const r = await adminApi(token, `/enquiries/${enquiryId}/requote`, { method: "POST", body: { inputs: {} } }); toast.success(`Revision ${r.revision} created on the current catalogue`); load(); } catch (err) { toast.error(err.message); }
  };
  const shown = d.quotes.find((q) => q.revision === viewRev);
  return (
    <div className="space-y-5" data-testid="enquiry-detail">
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="space-y-1">
          <p className="font-semibold text-base" data-testid="enquiry-detail-name">{c.name}</p>
          <p>{c.phone} · {c.email || "no email"}</p>
          <p>{c.city || "-"}, {c.country || "-"} · prefers {c.follow_up_method || "-"}</p>
          {c.alternative_email && <p>Alt. email: {c.alternative_email}</p>}
          {c.message && <p className="italic">“{c.message}”</p>}
          <p className="text-xs text-muted-foreground">Source: {e.source} · Permission: {e.contact_permission} · Marketing consent: {e.consent?.marketing ? "Yes" : "No"}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{d.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Assigned to</Label><Input data-testid="enquiry-assign" value={form.assigned_to} onChange={(x) => setForm({ ...form, assigned_to: x.target.value })} /></div>
          <div><Label>Next follow-up</Label><Input data-testid="enquiry-followup" type="date" value={form.next_follow_up} onChange={(x) => setForm({ ...form, next_follow_up: x.target.value })} /></div>
          <div className="col-span-2"><Label>Internal note</Label><Textarea data-testid="enquiry-note" rows={2} value={form.note} onChange={(x) => setForm({ ...form, note: x.target.value })} /></div>
          <Button className="col-span-2" onClick={save} data-testid="enquiry-save">Save</Button>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold">Quote {e.quote_id} — revisions</h4>
          <Button size="sm" variant="outline" onClick={requote} data-testid="enquiry-requote"><RefreshCw className="w-4 h-4 mr-1" />Recalculate on current catalogue (new revision)</Button>
        </div>
        <div className="space-y-3">
          {d.quotes.map((q) => (
            <div key={q.id} className="border rounded-md p-3 space-y-2" data-testid={`quote-rev-${q.revision}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <b>Rev {q.revision}</b><Badge className={statusTone[q.status]}>{q.status}</Badge>
                <span className="text-muted-foreground">pricing v{q.catalogue_version_no} · {q.created_at.slice(0, 16).replace("T", " ")} · {q.result.format.label} / {q.result.model.label}</span>
                <span>One-time {(q.result.sections.A.known_subtotal / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}{q.result.sections.A.has_pending ? "+" : ""} · Monthly {(q.result.sections.B.known_subtotal / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</span>
                <Button size="sm" variant="ghost" onClick={() => setViewRev(viewRev === q.revision ? null : q.revision)} data-testid={`quote-view-${q.revision}`}><Eye className="w-4 h-4 mr-1" />{viewRev === q.revision ? "Hide" : "Review"}</Button>
              </div>
              <QuoteActions token={token} q={q} enquiry={e} isSA={isSA} reload={load} />
            </div>
          ))}
        </div>
        {shown && <div className="mt-4 fq-root rounded-lg p-4">
          {shown.result.internal_terms?.length > 0 && <div className="fq-callout warn mb-4" data-testid="quote-internal-terms"><div><b>Internal source terms (admin only — need an agreement-level decision):</b><ul className="list-disc pl-4 text-xs">{shown.result.internal_terms.map((t) => <li key={t}>{t}</li>)}</ul></div></div>}
          <QuoteView result={shown.result} />
        </div>}
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-xs">
        <div><h4 className="font-semibold text-sm mb-1">Delivery log</h4>
          {d.deliveries.length ? d.deliveries.map((x) => <p key={x.id} data-testid="delivery-row">{x.at.slice(0, 16).replace("T", " ")} · {x.channel} · <b>{x.status}</b> {x.to ? `→ ${x.to}` : ""} {x.error ? `(${x.error})` : ""} · by {x.by}</p>) : <p className="text-muted-foreground">Nothing sent yet.</p>}
        </div>
        <div><h4 className="font-semibold text-sm mb-1">History & notes</h4>
          {[...(e.notes || []).map((n) => ({ at: n.at, by: n.by, action: "NOTE", detail: n.text })), ...(e.history || [])].sort((a, b) => (a.at < b.at ? 1 : -1)).map((h, i) => (
            <p key={i}>{h.at.slice(0, 16).replace("T", " ")} · {h.by} · <b>{h.action}</b> {h.detail}</p>
          ))}
        </div>
      </div>
    </div>
  );
}
